import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import {
  ANNUAL_OKR,
  ANNUAL_OKR_LIVE_REVIEW,
  ANNUAL_OKR_VIEW_OKRS,
} from "../../../../data/RouteUrls";
import useAnnualOkrStore, {
  useAnnualOkrStoreSelector,
} from "../_redux/AnnualOkrStore";
import AnnualOkrLiveReviewBody, {
  PresenceStrip,
} from "./AnnualOkrLiveReviewBody";
import LiveReviewInfoModal from "./LiveReviewInfoModal";
import SummaryGroups from "../../StrategyCanvas/SummaryPanel/SummaryPanel";
import { StandardTooltip } from "../../../shared/StandardTooltip/StandardTooltip";
import { InfoIcon } from "../../StrategyCanvas/StrategyCanvasHelpers";
import { useAnnualOkrLiveReviewStore } from "./_socket/AnnualOkrLiveReviewSocketStore";
import "./AnnualOkrLiveReview.scss";

// ─────────────────────────────────────────────────────────────────────────────
// Annual OKR Live Review — the coach's side of the joint session.
//
// ⚠️ THIS SCREEN IS A SHELL AROUND A SHARED BODY. Everything inside the header
// lives in `AnnualOkrLiveReviewBody`, which is byte-identical to the CEO app's
// copy — see its header for why. This file owns only what differs between the
// two apps: the coach's chrome (ProjectHeader, breadcrumbs, Back), and the
// `viewerRole` it passes down.
//
// It was a static shell until 2026-09-21: the layout existed but nothing ever
// called `connect()`, which is why the socket never opened and no data arrived.
// ─────────────────────────────────────────────────────────────────────────────

const selectClients = (state) => state.clients;
const selectRemaining = (state) => state.remarksRemaining;
const selectParticipants = (state) => state.participants;
const selectJoined = (state) => state.joined;

// Per-feature, not per-run: the explainer describes how the session works, and
// that does not change per client. Same key shape as `aokReviewInfoSeen` and
// EXW's `ebLiveReviewInfoSeen`.
const LIVE_INFO_SEEN_KEY = "aokLiveReviewInfoSeen";

const AnnualOkrLiveReview = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { annualOkrId } = useParams();
  const HeaderMetaData = useContext(HeaderContext);

  const clients = useAnnualOkrStoreSelector(selectClients);
  const client = (clients || []).find((c) => c.annualOkrId === annualOkrId);
  const companyName = client?.name || "";

  const remarksRemaining = useAnnualOkrLiveReviewStore(selectRemaining);
  const participants = useAnnualOkrLiveReviewStore(selectParticipants);
  const joined = useAnnualOkrLiveReviewStore(selectJoined);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Auto-opens the first time this coach reaches a live review, rather than
  // relying on them spotting the header affordance mid-meeting.
  useEffect(() => {
    if (!localStorage.getItem(LIVE_INFO_SEEN_KEY)) {
      setShowInfoModal(true);
      localStorage.setItem(LIVE_INFO_SEEN_KEY, "true");
    }
  }, []);

  useEffect(() => {
    document.title = "Art of Scale | Annual OKR Live Review";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Annual OKRs" });
    // The dashboard supplies the company name for the breadcrumb. Fetched here
    // too because a coach can land straight on this screen from the dashboard
    // CTA on a LIVE_REVIEW_STARTED client, or from a bookmark.
    if (!useAnnualOkrStore.getState().clients) {
      useAnnualOkrStore.getState().fetchDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ⚠️ The company name is an INERT crumb (`url: ""`), matching the review
  // screen's and EXW's. `ProjectHeader` renders a url-less crumb as text —
  // `<Link to="">` resolves to the app root and would quietly send the coach
  // home mid-meeting.
  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(ANNUAL_OKR_LIVE_REVIEW, [
        breadCrumbsArray[ANNUAL_OKR][0],
        {
          title: companyName ? `${companyName} Live Review` : "Live Review",
          url: "",
        },
      ]),
    );
  }, [dispatch, companyName]);

  // §7.5: locking is terminal and broadcasts to the room, so both parties leave
  // together. Back to the dashboard, where the client's stage now reads LOCKED.
  // ⚠️ Lands on the OKRs VIEW, not the dashboard. The run is finished and what
  // the coach wants next is the finished board — and the dashboard row they
  // came from now reads LOCKED, which is a worse answer than the board itself.
  // The dashboard is still refetched so that row is right when they go back.
  const handleLocked = useCallback(() => {
    useAnnualOkrStore.getState().fetchDashboard();
    history.push(ANNUAL_OKR_VIEW_OKRS.replace(":annualOkrId", annualOkrId));
  }, [history, annualOkrId]);

  return (
    <div>
      <ProjectHeader handleNavigation={() => history.push(ANNUAL_OKR)} showBack />

      <div id="annual-okr-live-review">
        {/* ⚠️ EXW's own `eblr-*` header classes, not a local set. An earlier
            pass used `aolr-header` / `aolr-live-badge` / `aolr-agenda-count`
            and then deleted the stylesheet that defined them, so the header
            rendered as unstyled stacked text. These come from
            `ExecutionBlueprintLiveReview.scss`, which the body imports. */}
        <div className="eblr-header">
          <div className="eblr-header-left">
            <div className="eblr-header-title">
              Live Review{companyName ? ` — ${companyName}` : ""}
            </div>

            {/* Inline beside the title, the way EXW does it — and only once
                joined, because `participants` is empty until the snapshot
                lands and rendering it early shows both people as "not joined"
                when the truth is that we don't know yet. */}
            {joined ? (
              <PresenceStrip participants={participants} viewerRole="coach" />
            ) : null}
          </div>
          <div className="eblr-header-right">
            <button
              type="button"
              className="eblr-info-icon"
              onClick={() => setShowInfoModal(true)}
            >
              <InfoIcon size={14} />
              <span>How live review works</span>
            </button>
            <div className="eblr-agenda-count">
              Agenda items:<strong> {remarksRemaining}</strong>
            </div>
          </div>
        </div>

        <div className="eblr-header-divider" />

        <AnnualOkrLiveReviewBody
          uniqueId={annualOkrId}
          viewerRole="coach"
          summaryGroupsComponent={SummaryGroups}
          tooltipComponent={StandardTooltip}
          // §7.1's `add_annual_okr_objective` requires an `owner_user_id`. The
          // coach is not a company user, so an Objective they add is owned by
          // the CEO — the participants array is where that id comes from.
          currentUserId={
            (participants || []).find((p) => p.role === "ceo")?.user_id ?? null
          }
          onLocked={handleLocked}
        />
      </div>
      {showInfoModal && (
        <LiveReviewInfoModal
          viewerRole="coach"
          summaryGroupsComponent={SummaryGroups}
          onClose={() => setShowInfoModal(false)}
        />
      )}
    </div>
  );
};

export default AnnualOkrLiveReview;
